import React, { useState } from 'react';
import classnames from 'classnames';
import './OpenSubmissions.scss';
import OpenSubmissionsToolbar from './toolbar/OpenSubmissionsToolbar';
import OpenSubmissionsGrid from './grid/OpenSubmissionsGrid';

import styleConstants from 'styles/variables.module.scss';
import openSubmissionsStyleConstants from './variables.module.scss';
import { useOpenSubmissionsConfigurationSelector } from './store/configuration/selectors';
import { useShowHideWidgetToolbar } from 'widgets/WidgetToolbarShowHidePanel/hooks';
import useWidgetStream from 'widgets/streaming/useWidgetStream';
import { usePortfolioStreamedOrSelectedPositionFromOrders } from 'portfolio/store/hooks';
import { useDelayedHover } from '@instinet-analytics/core-lib';
import { useWidgetContext } from 'widgets/WidgetContext';
import { GridApi } from '@instinet-analytics/semantic-ui-components';
import OpenSubmissionsChart from 'widgets/openSubmissions/OpenSubmissionsChart';
import { useOpenSubmissionsData } from 'widgets/openSubmissions/openSubmissionsData';
import { useSelectOpenSubmissionsStreamData } from 'widgets/openSubmissions/store/stream/selectors';

const padding = 2 * parseInt(styleConstants.spaceAround);
const borderWidth = 2 * parseInt(styleConstants.layoutBorderWidth);
const doughnutChartWidth = parseInt(
    openSubmissionsStyleConstants.doughnutChartWidth
);
const marginBetweenChartAndGrid = parseInt(
    openSubmissionsStyleConstants.marginBetweenChartAndGrid
);
const panelHeaderHeight = parseInt(styleConstants.panelHeaderHeight);

export default function OpenSubmissions() {
    const { widgetKey, width, height } = useWidgetContext();

    useWidgetStream();

    const selectedPosition = usePortfolioStreamedOrSelectedPositionFromOrders();

    const streamState = useSelectOpenSubmissionsStreamData(widgetKey);
    const { chartData, openSubmissionRows } =
        useOpenSubmissionsData(streamState);

    const [gridApi, setGridApi] = useState<GridApi | undefined>();

    const { hideGrid, hideDoughnut } =
        useOpenSubmissionsConfigurationSelector(widgetKey);

    const [onMouseEnter, onMouseLeave, isHovering] = useDelayedHover(0);

    const showChart = !hideDoughnut;
    const showGrid = !hideGrid;
    const { showToolbar } = useShowHideWidgetToolbar(!selectedPosition);

    const contentHeight =
        height - padding - borderWidth - (showToolbar ? panelHeaderHeight : 0);
    const chartWidth = showChart
        ? doughnutChartWidth + marginBetweenChartAndGrid
        : 0;
    const gridWidth = width - chartWidth - padding - borderWidth;

    return (
        <div
            className={classnames('OpenSubmissions')}
            data-widget-id={widgetKey}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <OpenSubmissionsToolbar
                widgetKey={widgetKey}
                widgetHeight={height}
                showChart={showChart}
                showGrid={showGrid}
                hovering={isHovering}
                gridApi={gridApi}
            />
            {selectedPosition && (
                <div className="chart-and-grid-container">
                    {showChart && (
                        <OpenSubmissionsChart
                            selectedPosition={selectedPosition}
                            allowHideDoughnut={!hideGrid}
                            data={chartData}
                        />
                    )}
                    {!hideGrid && (
                        <OpenSubmissionsGrid
                            widgetKey={widgetKey}
                            height={contentHeight}
                            width={gridWidth}
                            data={openSubmissionRows}
                            onGridApiSet={setGridApi}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
